import {
  Button,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  Textarea,
} from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import { POSTHOG_SURVEYS } from '../../constants/analytics';
import { safeAnalytics } from '../../utils/analytics';
import { useAppToast } from '../../hooks/ui/useAppToast';

interface FeedbackDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

const SURVEY_ID = POSTHOG_SURVEYS.QUICK_FEEDBACK;
const QUESTION_ID = 'd6347cf4-882a-4023-a88b-ed533204e111';

export default function FeedbackDialog({ isOpen, onClose }: FeedbackDialogProps) {
  const toast = useAppToast();
  const [feedback, setFeedback] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setFeedback('');
    }
  }, [isOpen]);

  const handleSubmit = () => {
    const trimmed = feedback.trim();
    if (!trimmed) return;

    setIsSubmitting(true);
    try {
      safeAnalytics.track('survey sent', {
        $survey_id: SURVEY_ID,
        [`$survey_response_${QUESTION_ID}`]: trimmed,
        $survey_questions: [
          {
            id: QUESTION_ID,
            question: 'How can we improve Bower for you?',
          },
        ],
        // Clean key for Slack webhook templates
        // (workspace, user, and page context auto-included via super properties)
        feedback_text: trimmed,
      } as any);

      toast({
        title: 'Thanks for your feedback!',
        description: 'We read every response.',
        status: 'success',
        duration: 3000,
      });
      onClose();
    } catch {
      toast({
        title: 'Failed to send',
        description: 'Please try again.',
        status: 'error',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size={{ base: 'full', md: 'md' }} isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader color="text.primary">Send feedback</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Text fontSize="sm" color="text.secondary" mb={3}>
            Bug reports, feature requests, or anything on your mind.
          </Text>
          <Textarea
            value={feedback}
            onChange={(e) => setFeedback(e.target.value)}
            placeholder="Tell us what's working, what's not, or what you'd love to see..."
            rows={6}
            resize="vertical"
            autoFocus
          />
        </ModalBody>
        <ModalFooter>
          <Button onClick={handleSubmit} isLoading={isSubmitting} isDisabled={!feedback.trim()}>
            Send feedback
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
